import React, { useRef, useState, useCallback, useEffect } from 'react';
import { NodeSelection } from '@tiptap/pm/state';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Extension } from '@tiptap/core';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import ResizableImage from './resizableImageExtension';
import Placeholder from '@tiptap/extension-placeholder';
import Mention from '@tiptap/extension-mention';
import { ReactRenderer } from '@tiptap/react';
import tippy from 'tippy.js';
import { TiptapMentionList } from './tiptapMentionList';
import { Colors, Images, KeyPurpose } from 'utils';
import { uploadFileWithPreview } from 'store';
import { MinLoader } from 'components';
import { useMentionOptions } from './useMentionOptions';
import { InlineField, inlineFieldIcon } from './inlineFieldExtension';
import { GoalBlock, TargetBlock } from './goalBlockExtension';
import { InlineFillContext } from './inlineFillContext';
import { InlineFieldErrorList, useInlineFieldErrors } from './inlineFieldsRichText';
import {
    createGoalBlock,
    createInlineField,
    inlineFieldKinds,
    inlineFieldTypes,
    inlineInputTypes,
    newInlineFieldId,
} from '../../../utils/inlineFields';

/** The toolbar's Insert row (template builder only). */
const INLINE_FIELD_BUTTONS = [
    { kind: inlineFieldKinds.INPUT, label: 'Input', shape: { type: inlineFieldTypes.INPUT } },
    { kind: inlineFieldKinds.DATE, label: 'Date', shape: { type: inlineFieldTypes.INPUT, defaultDateType: inlineInputTypes.DATE } },
    { kind: inlineFieldKinds.SELECT, label: 'Select', shape: { type: inlineFieldTypes.SELECT } },
    { kind: inlineFieldKinds.CHECK_BOX, label: 'Checkbox', shape: { type: inlineFieldTypes.CHECK_BOX } },
    { kind: inlineFieldKinds.RADIO_BUTTON, label: 'Radio', shape: { type: inlineFieldTypes.RADIO_BUTTON } },
];

const InsertButton = ({ icon, label, onClick, disabled, className = '' }) => (
    <button
        type="button"
        className={`tiptap-insert-btn ${className}`}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onClick}
        disabled={disabled}
    >
        <img src={icon} alt="" />
        {label}
    </button>
);

/** `onInsertGoalBlock` adds the Goal block button (progress reports / session notes); `extra` follows the buttons (the goal design's Target block). */
const InlineFieldInsertRow = ({ onInsert, onInsertGoalBlock, extra }) => (
    <div className="tiptap-toolbar tiptap-insert-row">
        <span className="tiptap-insert-row-label">Insert</span>
        {INLINE_FIELD_BUTTONS.map(({ kind, label, shape }) => (
            <InsertButton key={kind} icon={inlineFieldIcon(shape)} label={label} onClick={() => onInsert(kind)} />
        ))}
        {onInsertGoalBlock && (
            <InsertButton icon={Images.richTextGoalBlock} label="Goal block" onClick={onInsertGoalBlock} />
        )}
        {extra}
    </div>
);

// --- Custom FontSize Extension (inline via TextStyle) ---
const FontSize = Extension.create({
    name: 'fontSize',
    addOptions() {
        return { types: ['textStyle'] };
    },
    addGlobalAttributes() {
        return [
            {
                types: this.options.types,
                attributes: {
                    fontSize: {
                        default: null,
                        parseHTML: (element) => element.style.fontSize?.replace(/["']/g, '') || null,
                        renderHTML: (attributes) => {
                            if (!attributes.fontSize) return {};
                            return { style: `font-size: ${attributes.fontSize}` };
                        },
                    },
                },
            },
        ];
    },
    addCommands() {
        return {
            setFontSize:
                (fontSize) =>
                ({ chain }) => {
                    return chain().setMark('textStyle', { fontSize }).run();
                },
            unsetFontSize:
                () =>
                ({ chain }) => {
                    return chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run();
                },
        };
    },
});

const FONT_SIZE_OPTIONS = [
    { label: '12', value: '12px' },
    { label: '14', value: '14px' },
    { label: '16', value: '16px' },
    { label: '18', value: '18px' },
    { label: '20', value: '20px' },
    { label: '24', value: '24px' },
    { label: '28', value: '28px' },
    { label: '32', value: '32px' },
];

// --- Mention Suggestion ---
const createMentionSuggestion = (mentionItemsRef) => ({
    allowSpaces: true,
    allow: ({ state, range }) => {
        const text = state.doc.textBetween(range.from, range.to, ' ');
        if (/\s{2,}/.test(text)) return false;
        return true;
    },
    items: ({ query }) => {
        const mentionOptions = mentionItemsRef?.current || [];
        return mentionOptions
            .filter((item) => {
                const q = query.toLowerCase();
                return item?.name?.toLowerCase().includes(q) || item?.token?.toLowerCase().includes(q);
            })
            .map((item) => ({
                ...item,
                id: item?.defaultValue,
                label: item.token || item.name,
            }));
    },
    render: () => {
        let component;
        let popup;
        let backdrop;

        const removeBackdrop = () => {
            if (backdrop) {
                backdrop.remove();
                backdrop = null;
            }
        };

        const createBackdrop = () => {
            removeBackdrop();
            backdrop = document.createElement('div');
            backdrop.className = 'mention-back-drop';
            backdrop.style.position = 'fixed';
            backdrop.style.top = '0';
            backdrop.style.left = '0';
            backdrop.style.width = '100vw';
            backdrop.style.height = '100vh';
            backdrop.style.zIndex = '9998';
            backdrop.addEventListener('mousedown', () => {
                popup?.[0]?.hide();
                removeBackdrop();
            });
            document.body.appendChild(backdrop);
        };

        return {
            onStart: (props) => {
                component = new ReactRenderer(TiptapMentionList, {
                    props,
                    editor: props.editor,
                });

                if (!props.clientRect) return;

                popup = tippy('body', {
                    getReferenceClientRect: props.clientRect,
                    appendTo: () => document.body,
                    content: component.element,
                    showOnCreate: true,
                    interactive: true,
                    trigger: 'manual',
                    placement: 'bottom-start',
                    sticky: 'reference',
                    popperOptions: {
                        modifiers: [
                            {
                                name: 'preventOverflow',
                                options: {
                                    boundary: 'viewport',
                                },
                            },
                        ],
                    },
                });

                createBackdrop();
            },

            onUpdate(props) {
                component?.updateProps(props);

                if (!props.clientRect) return;

                popup?.[0]?.setProps({
                    getReferenceClientRect: props.clientRect,
                });
            },

            onKeyDown(props) {
                if (props.event.key === 'Escape') {
                    popup?.[0]?.hide();
                    removeBackdrop();
                    return true;
                }
                return component?.ref?.onKeyDown(props);
            },

            onExit() {
                popup?.[0]?.destroy();
                component?.destroy();
                removeBackdrop();
            },
        };
    },
});

// --- Toolbar Icons ---
const IconBold = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
        <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
    </svg>
);
const IconItalic = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <line x1="19" y1="4" x2="10" y2="4" />
        <line x1="14" y1="20" x2="5" y2="20" />
        <line x1="15" y1="4" x2="9" y2="20" />
    </svg>
);
const IconUnderline = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M6 3v7a6 6 0 0 0 6 6 6 6 0 0 0 6-6V3" />
        <line x1="4" y1="21" x2="20" y2="21" />
    </svg>
);
const IconAlignLeft = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <line x1="17" y1="10" x2="3" y2="10" />
        <line x1="21" y1="6" x2="3" y2="6" />
        <line x1="21" y1="14" x2="3" y2="14" />
        <line x1="17" y1="18" x2="3" y2="18" />
    </svg>
);
const IconAlignCenter = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <line x1="18" y1="10" x2="6" y2="10" />
        <line x1="21" y1="6" x2="3" y2="6" />
        <line x1="21" y1="14" x2="3" y2="14" />
        <line x1="18" y1="18" x2="6" y2="18" />
    </svg>
);
const IconAlignRight = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <line x1="21" y1="10" x2="7" y2="10" />
        <line x1="21" y1="6" x2="3" y2="6" />
        <line x1="21" y1="14" x2="3" y2="14" />
        <line x1="21" y1="18" x2="7" y2="18" />
    </svg>
);
const IconAlignJustify = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <line x1="21" y1="10" x2="3" y2="10" />
        <line x1="21" y1="6" x2="3" y2="6" />
        <line x1="21" y1="14" x2="3" y2="14" />
        <line x1="21" y1="18" x2="3" y2="18" />
    </svg>
);
const IconBulletList = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <line x1="8" y1="6" x2="21" y2="6" />
        <line x1="8" y1="12" x2="21" y2="12" />
        <line x1="8" y1="18" x2="21" y2="18" />
        <circle cx="4" cy="6" r="1" fill="currentColor" />
        <circle cx="4" cy="12" r="1" fill="currentColor" />
        <circle cx="4" cy="18" r="1" fill="currentColor" />
    </svg>
);
const IconOrderedList = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <line x1="10" y1="6" x2="21" y2="6" />
        <line x1="10" y1="12" x2="21" y2="12" />
        <line x1="10" y1="18" x2="21" y2="18" />
        <text x="2" y="8" fontSize="8" fill="currentColor" stroke="none" fontWeight="600">
            1
        </text>
        <text x="2" y="14" fontSize="8" fill="currentColor" stroke="none" fontWeight="600">
            2
        </text>
        <text x="2" y="20" fontSize="8" fill="currentColor" stroke="none" fontWeight="600">
            3
        </text>
    </svg>
);
const IconTextColor = ({ color }) => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M4 20h16" stroke={color || 'currentColor'} strokeWidth="3" />
        <path d="M9.5 4h5L18 16h-2l-1-3H9l-1 3H6L9.5 4z" />
    </svg>
);
const IconHighlight = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
    </svg>
);

// --- Toolbar Button ---
const ToolbarButton = ({ icon, title, isActive, onClick, disabled, children }) => (
    <button
        type="button"
        className={`tiptap-toolbar-btn ${isActive ? 'is-active' : ''}`}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onClick}
        disabled={disabled}
        title={title}
    >
        {icon || children}
    </button>
);

const ToolbarDivider = () => <div className="tiptap-toolbar-divider" />;

// --- Helper to check if an image is currently selected ---
const isImageSelected = (editor) => {
    if (!editor) return false;
    const { selection } = editor.state;
    if (selection instanceof NodeSelection) {
        return selection.node.type.name === 'image';
    }
    return false;
};

// --- Toolbar ---
const RichTextToolbar = ({ editor, readOnly, onImageClick, uploadLoader, onDeleteImage, imageSelected }) => {
    if (!editor || readOnly) return null;

    const currentTextColor = editor.getAttributes('textStyle').color || '#000000';
    const currentHighlightColor = editor.getAttributes('highlight').color || '#ffff00';

    return (
        <div className="tiptap-toolbar">
            <select
                className="tiptap-toolbar-select"
                value={editor.getAttributes('textStyle').fontSize || ''}
                onChange={(e) => {
                    const val = e.target.value;
                    if (val) {
                        editor.chain().focus().setFontSize(val).run();
                    } else {
                        editor.chain().focus().unsetFontSize().run();
                    }
                }}
            >
                <option value="">Font Size</option>
                {FONT_SIZE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                        {opt.label}
                    </option>
                ))}
            </select>

            <ToolbarDivider />

            <ToolbarButton
                icon={<IconBold />}
                title="Bold"
                isActive={editor.isActive('bold')}
                onClick={() => editor.chain().focus().toggleBold().run()}
            />
            <ToolbarButton
                icon={<IconItalic />}
                title="Italic"
                isActive={editor.isActive('italic')}
                onClick={() => editor.chain().focus().toggleItalic().run()}
            />
            <ToolbarButton
                icon={<IconUnderline />}
                title="Underline"
                isActive={editor.isActive('underline')}
                onClick={() => editor.chain().focus().toggleUnderline().run()}
            />

            <ToolbarDivider />

            <ToolbarButton
                icon={<IconAlignLeft />}
                title="Align Left"
                isActive={editor.isActive({ textAlign: 'left' })}
                onClick={() => editor.chain().focus().setTextAlign('left').run()}
            />
            <ToolbarButton
                icon={<IconAlignCenter />}
                title="Align Center"
                isActive={editor.isActive({ textAlign: 'center' })}
                onClick={() => editor.chain().focus().setTextAlign('center').run()}
            />
            <ToolbarButton
                icon={<IconAlignRight />}
                title="Align Right"
                isActive={editor.isActive({ textAlign: 'right' })}
                onClick={() => editor.chain().focus().setTextAlign('right').run()}
            />
            <ToolbarButton
                icon={<IconAlignJustify />}
                title="Justify"
                isActive={editor.isActive({ textAlign: 'justify' })}
                onClick={() => editor.chain().focus().setTextAlign('justify').run()}
            />

            <ToolbarDivider />

            <div className="tiptap-toolbar-color-wrapper" title="Text Color">
                <IconTextColor color={currentTextColor} />
                <input
                    type="color"
                    className="tiptap-toolbar-color-input"
                    value={currentTextColor}
                    onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
                />
            </div>
            <div className="tiptap-toolbar-color-wrapper" title="Highlight">
                <IconHighlight />
                <input
                    type="color"
                    className="tiptap-toolbar-color-input"
                    value={currentHighlightColor}
                    onChange={(e) => editor.chain().focus().toggleHighlight({ color: e.target.value }).run()}
                />
            </div>

            <ToolbarDivider />

            <ToolbarButton
                icon={<IconBulletList />}
                title="Bullet List"
                isActive={editor.isActive('bulletList')}
                onClick={() => editor.chain().focus().toggleBulletList().run()}
            />
            <ToolbarButton
                icon={<IconOrderedList />}
                title="Ordered List"
                isActive={editor.isActive('orderedList')}
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
            />

            <ToolbarDivider />

            <ToolbarButton title="Insert Image" onClick={onImageClick} disabled={uploadLoader}>
                {uploadLoader ? (
                    <MinLoader margin={'0'} color={'#0A0A0A'} position={'relative'} />
                ) : (
                    <img style={{ filter: 'brightness(0) saturate(100%)', width: 20, height: 20 }} src={Images.uploadImage} alt="img" />
                )}
            </ToolbarButton>

            {imageSelected && (
                <ToolbarButton title="Delete Image" onClick={onDeleteImage}>
                    <img src={Images.redTrashLine} alt="delete" style={{ width: 16, height: 16 }} />
                </ToolbarButton>
            )}

        </div>
    );
};

// --- Custom Mention Extension with data-token parsing ---
const CustomMention = Mention.extend({
    parseHTML() {
        return [
            {
                tag: 'span[data-type="mention"]',
                getAttrs: (dom) => {
                    const token = dom.getAttribute('data-token');
                    const id = dom.getAttribute('data-id') || dom.getAttribute('data-key');
                    const textContent = dom.textContent || '';
                    const label = token || textContent.replace(/^@/, '');
                    return {
                        id: id,
                        label: label,
                    };
                },
            },
            {
                tag: 'span.mention[data-id]',
                getAttrs: (dom) => {
                    if (dom.getAttribute('data-type') === 'mention') return false;
                    const id = dom.getAttribute('data-id') || dom.getAttribute('data-key');
                    const token = dom.getAttribute('data-token');
                    const textContent = dom.textContent || '';
                    const label = token || textContent.replace(/^@/, '');
                    return {
                        id: id,
                        label: label,
                    };
                },
            },
        ];
    },
});

// --- Main RichTextElement ---
//
// Canonical rich-text image architecture (2026-07-08 refactor):
//
//   The persisted HTML holds ONE marker for images:
//     <img data-key="orgId/clinical-docs/uuid.png" alt="filename.png">
//
//   No `src`. No sibling array. Signed URLs are ephemeral, minted per-
//   request by the backend, and delivered on the response envelope
//   under `imageUrls: {[key]: url}`. The client passes both `imageUrls`
//   (from the backend) and a locally-managed blob-URL map (for freshly
//   uploaded images before their first save-round-trip) into a resolver
//   function that the ResizableImage extension calls at render time.
//
//   Callers pass:
//     - `imageUrls` — { [key]: url } from the backend response
//     - No image state, no onImagesChange, no strip logic
//
// `fill = { disabled, hideStaffOnly, showErrors }`: a note's Rich Text holding
// inline fields / Goal blocks, being filled. Staff type and format the text as
// in any Rich Text; each field is its fill control and each Goal block its goal
// picker (`InlineFillContext`), values kept on the nodes, so `onChange` gets
// the html with them. Field errors are listed under the editor.
//
//   See docs/modules/file-service.md → "Rich-text image architecture".
export const RichTextElement = ({
    value,
    onChange,
    readOnly,
    activeSection,
    wrapperStyle = {},
    noMentions,
    item,
    imageUrls = {},
    onImageUpload,
    allowInlineFields = false,
    onSelectInlineField,
    allowGoalBlock = false,
    mentionOptions,
    targetBlock,
    fill,
}) => {
    const fileInputRef = useRef(null);
    const [uploadLoader, setUploadLoader] = useState(false);
    const [imageSelected, setImageSelected] = useState(false);
    const mentionItemsRef = useRef([]);
    const mentionSuggestionRef = useRef(null);
    const isInternalUpdate = useRef(false);
    const lastHtmlRef = useRef(value || '');
    const contextMentionOptions = useMentionOptions({ noMentions, activeSection, goalMode: !!item?.properties?.goalMode });
    // An editor outside the form builder's context (the Goal block design modal) gets its tokens from the caller.
    const currentModelEnums = mentionOptions || contextMentionOptions;
    const placeholderRef = useRef(item?.placeholder || '');
    placeholderRef.current = item?.placeholder || '';

    // Local blob-URL map for images uploaded during THIS session but
    // not yet saved. Populated in handleImageUpload after presign+PUT
    // returns the canonical StoredFile key; the resolver reads from
    // here for keys that don't appear in the backend `imageUrls` map
    // yet (i.e., first render before the first save-round-trip).
    // Kept in a ref (not state) — no re-render needed on mutation;
    // the resolver reads it live on the next paint.
    const blobUrlsRef = useRef({});

    // Resolver read by the extension's addNodeView. Prefers server-
    // provided imageUrls (canonical, signed, valid ~15 min). Falls
    // back to the local blob-URL map for freshly-uploaded images.
    // Backed by a ref so the resolver identity is stable across
    // renders — no need to re-configure the extension when either
    // map changes.
    const imageUrlsRef = useRef(imageUrls);
    imageUrlsRef.current = imageUrls;
    const resolveUrlRef = useRef((key) => {
        if (!key) return null;
        return blobUrlsRef.current[key] || imageUrlsRef.current[key] || null;
    });

    // Chip clicks go through a ref: the extension is configured once, when the editor is created.
    const onSelectInlineFieldRef = useRef(onSelectInlineField);
    onSelectInlineFieldRef.current = onSelectInlineField;
    // The goal design editor: `targetBlock = { onSelect }` offers the Target block and opens its design.
    const targetBlockRef = useRef(targetBlock);
    targetBlockRef.current = targetBlock;

    mentionItemsRef.current = currentModelEnums || [];
    if (!mentionSuggestionRef.current) {
        mentionSuggestionRef.current = createMentionSuggestion(mentionItemsRef);
    }

    const extensions = [
        StarterKit.configure({
            heading: false,
        }),
        Underline,
        TextAlign.configure({
            types: ['paragraph'],
        }),
        TextStyle,
        FontSize,
        Color,
        Highlight.configure({
            multicolor: true,
        }),
        ResizableImage.configure({
            inline: true,
            allowBase64: false,
            resolveUrl: (key) => resolveUrlRef.current(key),
        }),
        Placeholder.configure({
            placeholder: () => placeholderRef.current,
        }),
        // Always registered, so text holding inline fields keeps them on every surface;
        // only the template builder (`allowInlineFields`) offers the Insert row.
        // Filling a note (`fill`), they are controls staff fill and cannot delete.
        InlineField.configure({
            onSelect: (fieldId) => onSelectInlineFieldRef.current?.(fieldId),
            fill: !!fill,
        }),
        // A Goal block selects through the same callback (its id is unique among the text's fields),
        // and a click on its chip also opens its Goal design.
        GoalBlock.configure({
            onSelect: (blockId) => onSelectInlineFieldRef.current?.(blockId, { openDesign: true }),
            fill: !!fill,
        }),
        TargetBlock.configure({
            onSelect: () => targetBlockRef.current?.onSelect?.(),
        }),
        ...(noMentions
            ? []
            : [
                  CustomMention.configure({
                      HTMLAttributes: {
                          class: 'mention',
                      },
                      suggestion: mentionSuggestionRef.current,
                      renderHTML({ node }) {
                          const label = (node?.attrs?.label ?? node?.attrs?.token ?? node?.attrs?.name ?? '').replace(/^@/, '');
                          return [
                              'span',
                              {
                                  'data-id': node?.attrs?.id,
                                  'data-key': node?.attrs?.id,
                                  contenteditable: 'false',
                                  class: 'mention',
                              },
                              `@${label}`,
                          ];
                      },
                  }),
              ]),
    ];

    const editor = useEditor({
        extensions,
        content: value || '',
        editable: !readOnly,
        onUpdate: ({ editor }) => {
            // The ResizableImage extension's renderHTML deliberately
            // does NOT emit `src` — `getHTML()` returns HTML where
            // images are already just `<img data-key alt style>`. So
            // no strip pass is needed here; the outbound HTML is
            // the canonical persistence shape by construction.
            isInternalUpdate.current = true;
            const html = editor.getHTML();
            const normalized = html === '<p></p>' ? '' : html;
            lastHtmlRef.current = normalized;
            onChange(normalized);
        },
    });

    useEffect(() => {
        if (!editor) return;
        const handleSelectionUpdate = () => {
            setImageSelected(isImageSelected(editor));
        };
        editor.on('selectionUpdate', handleSelectionUpdate);
        editor.on('transaction', handleSelectionUpdate);
        return () => {
            editor.off('selectionUpdate', handleSelectionUpdate);
            editor.off('transaction', handleSelectionUpdate);
        };
    }, [editor]);

    useEffect(() => {
        if (editor) {
            editor.setEditable(!readOnly);
        }
    }, [readOnly, editor]);

    useEffect(() => {
        if (!editor) return;
        const normalizedValue = value || '';
        if (isInternalUpdate.current) {
            isInternalUpdate.current = false;
            // If the external value matches what we last produced, no update needed.
            // But if it differs, an external setValue() (e.g. auto-fill) changed it — apply it.
            if (normalizedValue === lastHtmlRef.current) {
                return;
            }
        }
        if (normalizedValue !== lastHtmlRef.current) {
            lastHtmlRef.current = normalizedValue;
            editor.commands.setContent(normalizedValue, false);
        }
    }, [value, editor]);

    // Trigger decoration re-evaluation when placeholder text changes
    useEffect(() => {
        if (!editor || editor.isDestroyed) return;
        // Dispatch an empty transaction to force decorations to re-evaluate
        // The Placeholder function reads from placeholderRef.current which is already updated above
        editor.view.dispatch(editor.state.tr);
    }, [editor, item?.placeholder]);

    const handleImageUpload = useCallback(
        async (event) => {
            const files = Array.from(event.target.files || []);
            if (!files.length || !editor) return;
            try {
                setUploadLoader(true);
                // Wait for presign + S3 PUT to complete BEFORE inserting
                // the node. Two reasons this is simpler than the older
                // "insert-then-updateAttributes" flow:
                //   1. No race between the initial insert transaction
                //      and the post-upload attr-stamp transaction —
                //      TipTap has bitten us multiple times here.
                //   2. The node is inserted ONCE with all final attrs
                //      (key, alt), so there's no ambiguous intermediate
                //      state to reconcile with the resolver map.
                //
                // Tradeoff: user waits ~1–3s for typical images before
                // seeing the preview. The uploadLoader spinner covers
                // that window. If we ever need instant preview, we can
                // presign upfront (fast, ~100ms) and insert with the
                // real key + a blob URL in the resolver, deferring the
                // PUT itself. Not needed today.
                for (const file of files) {
                    const { previewUrl, storedFile } = uploadFileWithPreview(file, KeyPurpose.CLINICAL_DOC);
                    try {
                        const uploaded = await storedFile;
                        // Seed the local resolver with the blob URL so
                        // the image renders before the next save-round-
                        // trip fills in the backend imageUrls map.
                        blobUrlsRef.current[uploaded.key] = previewUrl;
                        // Also lift the mapping into the shared context
                        // (imageUrls) so it survives THIS editor instance
                        // unmounting — switching the active attribute and
                        // coming back remounts a fresh RichTextElement with
                        // an empty blobUrlsRef, and the read-only preview
                        // resolves from the context map too. Without this
                        // the freshly-uploaded image breaks on the first
                        // remount, before any save round-trip.
                        onImageUpload?.(uploaded.key, previewUrl);
                        editor
                            .chain()
                            .focus()
                            .setImage({ key: uploaded.key, alt: uploaded.originalName || '' })
                            .run();
                    } catch (e) {
                        console.error('Image upload failed:', e);
                    }
                }
            } finally {
                setUploadLoader(false);
            }
            event.target.value = null;
        },
        [editor]
    );

    const deleteSelectedImage = useCallback(() => {
        if (!editor) return;
        const { selection } = editor.state;
        if (selection instanceof NodeSelection && selection.node.type.name === 'image') {
            editor.chain().focus().deleteSelection().run();
        }
    }, [editor]);

    const insertInlineField = useCallback(
        (kind) => {
            if (!editor) return;
            const fieldId = newInlineFieldId();
            editor.chain().focus().insertInlineField({ fieldId, field: createInlineField(kind) }).run();
            onSelectInlineFieldRef.current?.(fieldId);
        },
        [editor],
    );

    const insertGoalBlock = useCallback(() => {
        if (!editor) return;
        const blockId = newInlineFieldId();
        editor.chain().focus().insertGoalBlock({ blockId, block: createGoalBlock() }).run();
        onSelectInlineFieldRef.current?.(blockId);
    }, [editor]);

    // Filling a note: which field errors to show, read by the field nodes through `InlineFillContext`.
    const fillErrors = useInlineFieldErrors(fill ? value || '' : '', {
        hideStaffOnly: fill?.hideStaffOnly,
        showErrors: fill?.showErrors,
    });
    const fillContext = fill
        ? {
              disabled: !!fill.disabled,
              hideStaffOnly: !!fill.hideStaffOnly,
              showErrors: !!fill.showErrors,
              imageUrls,
              errorFor: fillErrors.errorFor,
              markTouched: fillErrors.markTouched,
          }
        : null;

    if (!editor) return null;

    // One Target block per goal design: a second would print every target twice.
    let hasTargetBlock = false;
    if (targetBlock) editor.state.doc.descendants((node) => (hasTargetBlock = hasTargetBlock || node.type.name === 'targetBlock'));

    return (
        <div style={{ ...wrapperStyle }} className="tiptap-editor-wrapper">
            <RichTextToolbar
                editor={editor}
                readOnly={readOnly}
                onImageClick={() => fileInputRef.current.click()}
                uploadLoader={uploadLoader}
                onDeleteImage={deleteSelectedImage}
                imageSelected={imageSelected}
            />
            {allowInlineFields && !readOnly && (
                <InlineFieldInsertRow
                    onInsert={insertInlineField}
                    onInsertGoalBlock={allowGoalBlock ? insertGoalBlock : undefined}
                    extra={
                        targetBlock && (
                            <InsertButton
                                icon={Images.attributeGoal}
                                label="Target block"
                                disabled={hasTargetBlock}
                                onClick={() => editor.chain().focus().insertTargetBlock().run()}
                            />
                        )
                    }
                />
            )}
            <input type="file" accept="image/*" ref={fileInputRef} style={{ display: 'none' }} onChange={handleImageUpload} />
            <InlineFillContext.Provider value={fillContext}>
                <EditorContent editor={editor} className="tiptap-editor-content" />
            </InlineFillContext.Provider>
            {fill && <InlineFieldErrorList errors={fillErrors.shownErrors} />}
        </div>
    );
};
